import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import {
  FILTER_MULTI_SELECT_NONE,
  isFilterMultiSelectAll,
  isFilterMultiSelectNone,
} from './filterMultiSelectUtils';
import { FILTER_INPUT_AUTOFILL_ATTRS, unlockFilterInputAfterFocus } from '../utils/filterInputAutofill';
import { UI_Z_CLASS } from '../utils/uiZIndex';

export type FilterMultiSelectOption = {
  id: string;
  label: string;
  subtitle?: string;
  /** Not available for the planned slot: row is dimmed, badge on the right, detail as tooltip. */
  unavailable?: { label: string; detail: string };
  /** Extra searchable text (not shown), e.g. Bestellnummer / Baustellenkontakt. */
  searchText?: string;
  leading?: React.ReactNode;
  groupId?: string;
};

export type FilterMultiSelectGroup = {
  id: string;
  label: string;
};

export type FilterMultiSelectProps = {
  options: FilterMultiSelectOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /**
   * emptyMeansAll: [] = alle ausgewählt (Toolbar-Filter).
   * explicit: [] = keine ausgewählt (Formular-Zuweisung).
   */
  selectionMode?: 'emptyMeansAll' | 'explicit';
  /** Optionale Gruppen-Header mit Checkbox (alle in der Gruppe an/aus). */
  groups?: FilterMultiSelectGroup[];
  /** Trigger wenn alle ausgewählt (emptyMeansAll). */
  allLabel?: string;
  /** Substantiv für Teilauswahl, z. B. „3 Mitarbeiter“ / „3 Projekte“. */
  countLabel?: string;
  /** Trigger im explicit-Modus bei leerer Auswahl. */
  assignLabel?: string;
  searchPlaceholder?: string;
  emptyListLabel?: string;
  showSubtitle?: boolean;
  icon?: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  width?: number;
  disabled?: boolean;
  ariaLabel?: string;
  /** Suchfeld ausblenden (z. B. bei wenigen festen Optionen). */
  showSearch?: boolean;
  /** „Keine auswählen“ / „Alle auswählen“ und Auswahl-Zähler ausblenden. */
  showBulkActions?: boolean;
  /** Kurzer Hinweis unter der Optionsliste. */
  panelHint?: string;
  /** start = Panel links am Trigger (öffnet nach rechts); end = rechts am Trigger (öffnet nach links). */
  panelAlign?: 'start' | 'end';
};

const VIEWPORT_MARGIN = 12;
const PANEL_GAP = 6;
const PREFERRED_MAX_HEIGHT = 380;
const MIN_PANEL_HEIGHT = 120;

function panelPosition(
  triggerRect: DOMRect,
  panelWidth: number,
  align: 'start' | 'end',
): { top: number; left: number; width: number; maxHeight: number } {
  const width = Math.min(panelWidth, window.innerWidth - VIEWPORT_MARGIN * 2);
  const spaceBelow = window.innerHeight - triggerRect.bottom - VIEWPORT_MARGIN - PANEL_GAP;
  const spaceAbove = triggerRect.top - VIEWPORT_MARGIN - PANEL_GAP;
  const opensAbove =
    spaceBelow < Math.min(PREFERRED_MAX_HEIGHT, 200) && spaceAbove > spaceBelow;

  const maxHeight = Math.max(
    MIN_PANEL_HEIGHT,
    Math.min(PREFERRED_MAX_HEIGHT, opensAbove ? spaceAbove : spaceBelow),
  );
  const top = opensAbove
    ? Math.max(VIEWPORT_MARGIN, triggerRect.top - PANEL_GAP - maxHeight)
    : triggerRect.bottom + PANEL_GAP;

  let left = align === 'start' ? triggerRect.left : triggerRect.right - width;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - VIEWPORT_MARGIN - width));
  return { top, left, width, maxHeight };
}

function CheckMark() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CheckboxVisual({
  checked,
  indeterminate = false,
}: {
  checked: boolean;
  indeterminate?: boolean;
}) {
  return (
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px] ${
        checked || indeterminate ? 'border-pipedrive-blue bg-pipedrive-blue' : 'border-app-border-strong bg-app-surface'
      }`}
      aria-hidden
    >
      {indeterminate && !checked ? (
        <span className="h-0.5 w-2 rounded-sm bg-app-surface" />
      ) : checked ? (
        <CheckMark />
      ) : null}
    </span>
  );
}

export function FilterMultiSelect({
  options,
  selected,
  onChange,
  selectionMode = 'emptyMeansAll',
  groups,
  allLabel = 'Alle',
  countLabel = 'Einträge',
  assignLabel = 'Auswahl',
  searchPlaceholder = 'Suchen…',
  emptyListLabel = 'Keine Einträge',
  showSubtitle = false,
  icon = null,
  className = '',
  triggerClassName = '',
  width = 320,
  disabled = false,
  ariaLabel = 'Auswahl filtern',
  showSearch = true,
  showBulkActions = true,
  panelHint,
  panelAlign = 'end',
}: FilterMultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [searchAutofillLocked, setSearchAutofillLocked] = React.useState(true);
  const [panelStyle, setPanelStyle] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const searchFieldId = React.useId();
  const panelRef = React.useRef<HTMLDivElement>(null);

  const updatePanelPosition = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanelStyle(panelPosition(rect, width, panelAlign));
  }, [panelAlign, width]);

  const total = options.length;
  const noneSelected = selectionMode === 'emptyMeansAll' && isFilterMultiSelectNone(selected);
  const allSelected =
    selectionMode === 'explicit'
      ? total > 0 && selected.length === total
      : isFilterMultiSelectAll(selected, total);
  const explicitCount =
    selectionMode === 'explicit'
      ? selected.length
      : noneSelected
        ? 0
        : selected.length === 0
          ? total
          : selected.length;

  React.useEffect(() => {
    if (!open) {
      setSearchAutofillLocked(true);
      return;
    }
    setQuery('');
    setSearchAutofillLocked(true);
  }, [open]);

  React.useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label} ${o.subtitle ?? ''} ${o.searchText ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const groupedSections = React.useMemo(() => {
    if (!groups || groups.length === 0) {
      return [{ group: null as FilterMultiSelectGroup | null, items: filtered }];
    }
    const byGroup = new Map<string, FilterMultiSelectOption[]>();
    const ungrouped: FilterMultiSelectOption[] = [];
    for (const opt of filtered) {
      if (opt.groupId) {
        const list = byGroup.get(opt.groupId) ?? [];
        list.push(opt);
        byGroup.set(opt.groupId, list);
      } else {
        ungrouped.push(opt);
      }
    }
    const sections: Array<{ group: FilterMultiSelectGroup | null; items: FilterMultiSelectOption[] }> = [];
    for (const group of groups) {
      const items = byGroup.get(group.id) ?? [];
      if (items.length === 0) continue;
      sections.push({ group, items });
    }
    if (ungrouped.length > 0) sections.push({ group: null, items: ungrouped });
    return sections;
  }, [filtered, groups]);

  const isChecked = (id: string) => {
    if (selectionMode === 'explicit') return selected.includes(id);
    if (noneSelected) return false;
    return allSelected || selected.includes(id);
  };

  const currentSelectedSet = () => {
    if (selectionMode === 'explicit') return new Set(selected);
    if (noneSelected) return new Set<string>();
    if (selected.length === 0) return new Set(options.map((o) => o.id));
    return new Set(selected);
  };

  const commitSelection = (next: Set<string>) => {
    if (selectionMode === 'explicit') {
      onChange([...next]);
      return;
    }
    if (next.size === options.length) onChange([]);
    else if (next.size === 0) onChange([FILTER_MULTI_SELECT_NONE]);
    else onChange([...next]);
  };

  const toggle = (id: string) => {
    const set = currentSelectedSet();
    if (set.has(id)) set.delete(id);
    else set.add(id);
    commitSelection(set);
  };

  const toggleGroup = (groupId: string) => {
    const groupOptionIds = options.filter((o) => o.groupId === groupId).map((o) => o.id);
    if (groupOptionIds.length === 0) return;
    const set = currentSelectedSet();
    const allOn = groupOptionIds.every((id) => set.has(id));
    if (allOn) {
      for (const id of groupOptionIds) set.delete(id);
    } else {
      for (const id of groupOptionIds) set.add(id);
    }
    commitSelection(set);
  };

  const groupCheckState = (groupId: string): boolean | 'indeterminate' => {
    const groupOptionIds = options.filter((o) => o.groupId === groupId).map((o) => o.id);
    if (groupOptionIds.length === 0) return false;
    const checkedCount = groupOptionIds.filter((id) => isChecked(id)).length;
    if (checkedCount === 0) return false;
    if (checkedCount === groupOptionIds.length) return true;
    return 'indeterminate';
  };

  const selectAll = () => {
    if (selectionMode === 'explicit') onChange(options.map((o) => o.id));
    else onChange([]);
  };

  const deselectAll = () => {
    if (selectionMode === 'explicit') onChange([]);
    else onChange([FILTER_MULTI_SELECT_NONE]);
  };

  const triggerLabel =
    selectionMode === 'explicit'
      ? explicitCount === 0
        ? assignLabel
        : `${assignLabel} · ${explicitCount}`
      : noneSelected
        ? `0 ${countLabel}`
        : allSelected
          ? allLabel
          : `${explicitCount} ${countLabel}`;

  const selectionSummary =
    selectionMode === 'explicit'
      ? `${explicitCount} / ${total} ausgewählt`
      : noneSelected
        ? `0 / ${total} ausgewählt`
        : allSelected
          ? `Alle / ${total} ausgewählt`
          : `${explicitCount} / ${total} ausgewählt`;

  const renderOption = (opt: FilterMultiSelectOption) => {
    const checked = isChecked(opt.id);
    return (
      <button
        key={opt.id}
        type="button"
        onClick={() => toggle(opt.id)}
        className="flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left hover:bg-app-hover"
        title={opt.unavailable?.detail}
      >
        <CheckboxVisual checked={checked} />
        {opt.leading ? (
          <span className={`shrink-0 ${opt.unavailable ? 'opacity-60' : ''}`}>{opt.leading}</span>
        ) : null}
        {showSubtitle && opt.subtitle ? (
          <span className={`min-w-0 flex-1 ${opt.unavailable ? 'opacity-60' : ''}`}>
            <span className="block truncate text-sm text-app-text">{opt.label}</span>
            <span className="block truncate text-[11px] text-app-text-subtle">{opt.subtitle}</span>
          </span>
        ) : (
          <span className={`min-w-0 flex-1 truncate text-sm text-app-text ${opt.unavailable ? 'opacity-60' : ''}`}>
            {opt.label}
          </span>
        )}
        {opt.unavailable ? (
          <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-medium leading-none text-amber-800">{opt.unavailable.label}</span>
        ) : null}
      </button>
    );
  };

  const panel =
    open && panelStyle ? (
      <div
        ref={panelRef}
        data-filter-multi-select-panel
        className={`fixed ${UI_Z_CLASS.portalPanel} flex flex-col overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-lg`}
        style={{
          top: panelStyle.top,
          left: panelStyle.left,
          width: panelStyle.width,
          maxHeight: panelStyle.maxHeight,
        }}
        role="dialog"
        aria-label={ariaLabel}
      >
        {showSearch ? (
          <div className="shrink-0 border-b border-app-border p-2.5">
            <div className="flex items-center gap-2 rounded-md border border-app-border bg-app-bg px-2.5 py-1.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
              <input
                id={searchFieldId}
                name={`filter-multi-search-${searchFieldId}`}
                type="text"
                role="searchbox"
                aria-label={searchPlaceholder}
                {...FILTER_INPUT_AUTOFILL_ATTRS}
                readOnly={searchAutofillLocked}
                onFocus={(e) => {
                  unlockFilterInputAfterFocus(e.currentTarget);
                  setSearchAutofillLocked(false);
                }}
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 border-0 bg-transparent text-xs text-app-text-muted outline-none placeholder:text-slate-400"
              />
            </div>
          </div>
        ) : null}
        {showBulkActions ? (
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-app-border px-2.5 py-1.5 text-[11px] text-app-text-subtle">
            <span className="min-w-0 truncate">{selectionSummary}</span>
            <span className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={deselectAll}
                disabled={selectionMode === 'explicit' ? explicitCount === 0 : noneSelected}
                className="font-medium text-pipedrive-blue hover:underline disabled:cursor-default disabled:text-slate-300 disabled:no-underline"
              >
                Keine auswählen
              </button>
              <button
                type="button"
                onClick={selectAll}
                disabled={selectionMode === 'explicit' ? allSelected : allSelected && !noneSelected}
                className="font-medium text-pipedrive-blue hover:underline disabled:cursor-default disabled:text-slate-300 disabled:no-underline"
              >
                Alle auswählen
              </button>
            </span>
          </div>
        ) : null}
        <div className={`app-scrollbar-subtle min-h-0 flex-1 overflow-y-auto py-0.5 ${!showSearch && !showBulkActions ? 'pt-1' : ''}`}>
          {filtered.length === 0 ? (
            <p className="px-2.5 py-4 text-center text-xs text-slate-400">
              {total === 0 ? emptyListLabel : 'Keine Treffer'}
            </p>
          ) : (
            groupedSections.map((section) => (
              <div key={section.group?.id ?? '__ungrouped'}>
                {section.group ? (
                  <button
                    type="button"
                    onClick={() => toggleGroup(section.group!.id)}
                    className="flex w-full items-center gap-2.5 border-b border-app-border bg-app-surface-muted/80 px-2.5 py-1.5 text-left hover:bg-app-hover"
                  >
                    <CheckboxVisual
                      checked={groupCheckState(section.group.id) === true}
                      indeterminate={groupCheckState(section.group.id) === 'indeterminate'}
                    />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-semibold uppercase tracking-wide text-app-text-subtle">
                      {section.group.label}
                    </span>
                    <span className="text-[11px] text-slate-400">{section.items.length}</span>
                  </button>
                ) : null}
                {section.items.map(renderOption)}
              </div>
            ))
          )}
        </div>
        {panelHint ? (
          <p className="shrink-0 border-t border-app-border px-2.5 py-2 text-[11px] leading-snug text-app-text-subtle">
            {panelHint}
          </p>
        ) : null}
      </div>
    ) : null;

  return (
    <div className={className}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (!open) {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (rect) setPanelStyle(panelPosition(rect, width, panelAlign));
          }
          setOpen((v) => !v);
        }}
        className={
          triggerClassName ||
          'inline-flex items-center gap-2 rounded-md border border-app-border bg-app-surface px-2.5 py-1.5 text-xs font-medium text-app-text-muted hover:bg-app-hover disabled:opacity-50'
        }
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {icon ? <span className="shrink-0 text-app-text-subtle">{icon}</span> : null}
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-app-text-subtle" aria-hidden />
      </button>
      {open && typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </div>
  );
}
